export type color = { r: number; g: number; b: number };
export const mul = (color: color, f: number) => ({ r: Math.round(color.r * f), g: Math.round(color.g * f), b: Math.round(color.b * f) });
export const mix = (...colors: color[]) => ({
  r: colors.reduce((prev, next) => prev + next.r, 0),
  g: colors.reduce((prev, next) => prev + next.g, 0),
  b: colors.reduce((prev, next) => prev + next.b, 0),
});

export const COLORS = ["blue", "green", "red", "yellow"] as const;

export const getAllColors = function () {
  return COLORS;
};

export const getColorFromIndex = function (id: number) {
  return COLORS[id % COLORS.length];
};

export const getColorFromHex = function (hex: Nullable<string>) {
  if (!hex) return "white";

  switch (hex.toUpperCase()) {
    case "#2652C7":
      return "blue";

    case "#F3B13F":
      return "yellow";

    case "#65A948":
      return "green";

    case "#D12D1E":
      return "red";

    default:
      return "white";
  }
};

export const getHexFromColor = function (color: string, saturation: number = 300) {
  if (color == "white") return "#FFFFFF";

  // Map saturation to available shades (100, 200, 300)
  let shade = "300";
  if (saturation <= 150) shade = "100";
  else if (saturation <= 250) shade = "200";
  else shade = "300";

  const hex = getColorHex(`--color-${color}-${shade}`).trim();

  if (hex) return hex;

  return getColorHex("--color-primary").trim();
};

export const getColorHex = (cssVariable: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(cssVariable).replace(" ", "");
};

export const getColorsHex = () => {
  return COLORS.map((color) => {
    return getColorHex(`--color-${color}-300`);
  });
};

export const getColorNamesRepeating = (n: number) => {
  const a: string[] = [];

  let i = 0;

  for (let j = 0; j < n; j++) {
    if (i >= COLORS.length) {
      i = 0;
    }

    a.push(COLORS[i]);

    i++;
  }

  return a;
};

// Determistic get a color from COLORS array based on a string
export const getColorFromString = (str: string) => {
  const hash = str.split("").reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);

  return getColorFromIndex(hash);
};

// Convert RGB color to hex format for Excel export
export const rgbToHex = (rgbOrHex: { r: number; g: number; b: number } | string): string => {
  let r: number, g: number, b: number;
  if (typeof rgbOrHex === "string") {
    if (rgbOrHex.includes("#")) {
      // Already in hex format
      return rgbOrHex.replace("#", "").toUpperCase();
    }
    // Parse rgb(r,g,b) string format - supports decimals
    const match = rgbOrHex.match(/rgb\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)\)/);
    if (match) {
      r = Math.round(parseFloat(match[1]));
      g = Math.round(parseFloat(match[2]));
      b = Math.round(parseFloat(match[3]));
    } else {
      console.log("Invalid RGB string format:", rgbOrHex);

      throw new Error("Invalid RGB string format");
    }
  } else {
    // Handle {r, g, b} object format
    ({ r, g, b } = rgbOrHex);
  }

  return [r, g, b]
    .map((val) => val.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
};
